// Diagnostic workup data for each enemy.
// Each entry maps enemy id → { presentation, tests, diagnosisChoices, correctDiagnosis }

export const diagnostics = {
  "strep-pneumo": {
    presentation: {
      intro: "A 65-year-old man presents with 3 days of fever, productive rust-coloured sputum, and sharp chest pain that worsens with breathing.",
      clues: ["Temp 39.1°C, HR 105, RR 24, SpO2 93% on room air", "Dullness to percussion right lower lobe", "Bronchial breath sounds and egophony right base"],
    },
    tests: [
      { id: "sputum-gram", name: "Sputum Gram Stain", icon: "🔬", clue: "Gram-positive lancet-shaped diplococci surrounded by white blood cells. Classic appearance!" },
      { id: "urine-antigen", name: "Urine Pneumococcal Antigen", icon: "🧪", clue: "POSITIVE — highly specific for S. pneumoniae. Rapid and doesn't require sputum." },
      { id: "cxr-pneumo", name: "Chest X-Ray", icon: "🩻", clue: "Dense lobar consolidation of the right lower lobe. Air bronchograms visible." },
      { id: "blood-cx-pneumo", name: "Blood Culture", icon: "🧫", clue: "Growing gram-positive diplococci at 24 hours — bacteraemia confirmed!" },
      { id: "cbc-pneumo", name: "CBC", icon: "🩸", clue: "WBC 18,400 with 88% neutrophils. CRP 220 mg/L. Significant systemic infection." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Streptococcus pneumoniae (Pneumococcal Pneumonia)" },
      { id: "wrong1", name: "Mycoplasma pneumoniae (Atypical Pneumonia)" },
      { id: "wrong2", name: "Legionella pneumophila (Legionnaires' Disease)" },
      { id: "wrong3", name: "Mycobacterium tuberculosis (Pulmonary TB)" },
    ],
    correctDiagnosis: "correct",
  },

  "ecoli-uti": {
    presentation: {
      intro: "A 28-year-old woman presents with 2 days of burning on urination, urgency, and suprapubic discomfort. No fever or flank pain.",
      clues: ["Afebrile, BP and HR normal", "Suprapubic tenderness on palpation", "No costovertebral angle tenderness (uncomplicated)"],
    },
    tests: [
      { id: "dipstick", name: "Urine Dipstick", icon: "🧪", clue: "Leukocyte esterase POSITIVE, nitrites POSITIVE. Nitrites indicate gram-negative Enterobacteriaceae!" },
      { id: "urine-micro", name: "Urine Microscopy", icon: "🔬", clue: ">100 WBCs/hpf. Gram-negative rods seen. No yeast or trichomonads." },
      { id: "urine-cx", name: "Urine Culture", icon: "🧫", clue: ">100,000 CFU/mL of a single gram-negative organism. Sensitivity pending." },
      { id: "koh-prep", name: "KOH Preparation", icon: "🔬", clue: "NEGATIVE — no yeast forms or hyphae. Fungal UTI (Candida) excluded." },
      { id: "cbc-uti", name: "CBC", icon: "🩸", clue: "Mild leukocytosis 12,500. No systemic sepsis. Consistent with uncomplicated UTI." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "E. coli — Uncomplicated UTI" },
      { id: "wrong1", name: "Candida albicans — Fungal UTI" },
      { id: "wrong2", name: "Chlamydia trachomatis — Cervicitis" },
      { id: "wrong3", name: "Trichomonas vaginalis — Trichomoniasis" },
    ],
    correctDiagnosis: "correct",
  },

  "mrsa": {
    presentation: {
      intro: "A 35-year-old gym instructor presents with a rapidly enlarging, painful, red, and fluctuant lump on the thigh. Fever 38.6°C for 2 days.",
      clues: ["Temp 38.6°C, HR 98", "8cm area of erythema, warmth, and induration on right thigh", "Central necrotic pustule with surrounding cellulitis"],
    },
    tests: [
      { id: "wound-cx", name: "Wound Swab Culture", icon: "🧫", clue: "Heavy growth of Staphylococcus aureus. Gram stain: gram-positive cocci in clusters." },
      { id: "oxacillin-disk", name: "Oxacillin Disk Diffusion", icon: "🔬", clue: "Zone of inhibition 8mm — RESISTANT. This is not MSSA!" },
      { id: "meca-pcr", name: "mecA Gene PCR", icon: "🧬", clue: "mecA gene DETECTED — confirms methicillin resistance. This is MRSA." },
      { id: "blood-cx-mrsa", name: "Blood Culture", icon: "🧫", clue: "Gram-positive cocci in clusters growing — S. aureus bacteraemia confirmed!" },
      { id: "nasal-screen", name: "MRSA Nasal Swab Screen", icon: "👃", clue: "MRSA positive from nares. Patient is a carrier — likely source of the skin infection." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "MRSA (Methicillin-Resistant S. aureus)" },
      { id: "wrong1", name: "MSSA (Methicillin-Sensitive S. aureus)" },
      { id: "wrong2", name: "Group A Streptococcus — Cellulitis" },
      { id: "wrong3", name: "Pseudomonas aeruginosa — Wound Infection" },
    ],
    correctDiagnosis: "correct",
  },

  "influenza-a": {
    presentation: {
      intro: "A 45-year-old teacher presents in January with abrupt-onset fever, severe myalgias, headache, and dry cough since yesterday.",
      clues: ["Temp 39.5°C, HR 102, looks unwell", "Diffuse myalgias, photophobia", "Clear nasal discharge, no pharyngeal exudate"],
    },
    tests: [
      { id: "rapid-flu", name: "Rapid Influenza Antigen Test", icon: "🧪", clue: "POSITIVE for Influenza A. Results in 15 minutes at the bedside." },
      { id: "np-pcr", name: "Nasopharyngeal PCR", icon: "🧬", clue: "Influenza A H3N2 confirmed by PCR. Most sensitive test available." },
      { id: "cbc-flu", name: "CBC", icon: "🩸", clue: "Lymphopenia noted. WBC 5,200 — normal-to-low count is typical for viral infections (not bacterial)." },
      { id: "cxr-flu", name: "Chest X-Ray", icon: "🩻", clue: "Clear lung fields — uncomplicated influenza, no secondary pneumonia yet." },
      { id: "blood-cx-flu", name: "Blood Culture", icon: "🧫", clue: "No growth at 48 hours — confirms viral aetiology rather than primary bacteraemia." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Influenza A" },
      { id: "wrong1", name: "SARS-CoV-2 (COVID-19)" },
      { id: "wrong2", name: "Rhinovirus (Common Cold)" },
      { id: "wrong3", name: "Streptococcus pneumoniae Pneumonia" },
    ],
    correctDiagnosis: "correct",
  },

  "tb": {
    presentation: {
      intro: "A 32-year-old recent immigrant presents with a 3-month history of productive cough, drenching night sweats, 8kg weight loss, and haemoptysis.",
      clues: ["Temp 37.8°C (low-grade), HR 90", "Decreased breath sounds and dullness right upper zone", "Cachexia and cervical lymphadenopathy"],
    },
    tests: [
      { id: "cxr-tb", name: "Chest X-Ray", icon: "🩻", clue: "Cavitary lesion in the right upper lobe with fibrotic streaking. Classic for reactivation TB!" },
      { id: "afb-smear", name: "Sputum AFB Smear (Ziehl-Neelsen)", icon: "🔬", clue: "3+ acid-fast bacilli seen — red rods against blue background. Highly infectious!" },
      { id: "genexpert", name: "GeneXpert MTB/RIF PCR", icon: "🧬", clue: "M. tuberculosis detected. Rifampin resistance NOT detected — drug-sensitive TB." },
      { id: "tst", name: "Tuberculin Skin Test (TST)", icon: "💉", clue: "22mm induration at 72 hours. Strongly positive — significant TB exposure/infection." },
      { id: "igra", name: "IGRA (QuantiFERON-TB Gold)", icon: "🧪", clue: "POSITIVE — interferon-gamma response confirms M. tuberculosis infection." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Mycobacterium tuberculosis — Pulmonary TB" },
      { id: "wrong1", name: "Lung Adenocarcinoma" },
      { id: "wrong2", name: "MAC (Mycobacterium avium Complex)" },
      { id: "wrong3", name: "Histoplasma capsulatum" },
    ],
    correctDiagnosis: "correct",
  },

  "candida": {
    presentation: {
      intro: "A 58-year-old post-bone marrow transplant patient remains febrile despite 5 days of broad-spectrum antibiotics. No localising source found.",
      clues: ["Temp 38.9°C persisting on antibiotics (red flag!)", "Central venous line in situ × 3 weeks", "Mild visual blurring reported by patient"],
    },
    tests: [
      { id: "blood-cx-candida", name: "Blood Culture", icon: "🧫", clue: "Yeast growing in aerobic bottle at 36 hours. Germ tube test POSITIVE — consistent with C. albicans." },
      { id: "beta-glucan", name: "Beta-D-Glucan", icon: "🧪", clue: "Markedly elevated at 580 pg/mL (normal <80). Sensitive marker for invasive fungal infection." },
      { id: "ophthalmology", name: "Ophthalmology Exam", icon: "👁️", clue: "Retinal white fluffy exudates — candidal endophthalmitis. Changes management (longer treatment)!" },
      { id: "koh-candida", name: "KOH Preparation", icon: "🔬", clue: "Pseudohyphae and budding yeast seen — Candida morphology confirmed." },
      { id: "echo-candida", name: "Echocardiogram", icon: "🫀", clue: "No vegetations. Cardiac source excluded. Line-associated candidaemia most likely." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Candida albicans — Invasive Candidaemia" },
      { id: "wrong1", name: "Aspergillus fumigatus — Invasive Aspergillosis" },
      { id: "wrong2", name: "Cryptococcus neoformans" },
      { id: "wrong3", name: "Gram-negative Bacteraemia" },
    ],
    correctDiagnosis: "correct",
  },

  "plasmodium": {
    presentation: {
      intro: "A 25-year-old backpacker returns from 3 weeks in Sub-Saharan Africa. Presents with 48-hour cyclical fevers, rigors, severe headache, and jaundice.",
      clues: ["Temp 40.1°C, HR 118, BP 95/60 (shocked)", "Jaundice, splenomegaly", "Reports NO malaria prophylaxis taken"],
    },
    tests: [
      { id: "blood-smear", name: "Thick & Thin Blood Smear", icon: "🔬", clue: "Ring forms inside RBCs + banana-shaped gametocytes. Multiple infected cells per RBC. Parasitaemia 8%!" },
      { id: "malaria-rdt", name: "Malaria RDT (Rapid Diagnostic Test)", icon: "🧪", clue: "P. falciparum-specific HRP-2 antigen POSITIVE. Result in 20 minutes." },
      { id: "malaria-pcr", name: "Malaria PCR", icon: "🧬", clue: "P. falciparum confirmed, P. vivax excluded. Most sensitive species identification." },
      { id: "cbc-malaria", name: "CBC", icon: "🩸", clue: "Hb 72 g/L (severe anaemia), platelets 38,000 (thrombocytopenia). Classic haematological triad of severe malaria." },
      { id: "lft-malaria", name: "Liver Function Tests", icon: "🧪", clue: "Bilirubin 85 μmol/L (elevated — haemolysis). ALT mildly elevated. Consistent with malaria." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Plasmodium falciparum — Severe Malaria" },
      { id: "wrong1", name: "Dengue Fever" },
      { id: "wrong2", name: "Typhoid Fever (Salmonella typhi)" },
      { id: "wrong3", name: "Plasmodium vivax — Malaria" },
    ],
    correctDiagnosis: "correct",
  },

  "cdiff": {
    presentation: {
      intro: "A 72-year-old, day 10 post hip replacement, on cefazolin prophylaxis. Now with 8 episodes of watery diarrhoea, severe abdominal cramps, and fever.",
      clues: ["Temp 38.3°C, HR 108, mild hypotension", "Diffuse abdominal tenderness, hyperactive bowel sounds", "Recent antibiotics are the key clue!"],
    },
    tests: [
      { id: "cdiff-pcr", name: "Stool C. diff Toxin PCR", icon: "🧬", clue: "POSITIVE for C. difficile toxin genes. Most sensitive and specific test — gold standard." },
      { id: "stool-cx", name: "Stool Culture", icon: "🧫", clue: "Anaerobic culture growing C. difficile with characteristic horse-manure odour." },
      { id: "cbc-cdiff", name: "CBC", icon: "🩸", clue: "WBC 28,000 — markedly elevated. 'Toxic' white count in C. diff can signal severe or fulminant disease." },
      { id: "ct-cdiff", name: "CT Abdomen", icon: "🩻", clue: "Colonic wall thickening, pericolonic fat stranding. 'Thumbprinting' appearance — pseudomembranous colitis." },
      { id: "colonoscopy", name: "Flexible Sigmoidoscopy", icon: "🔭", clue: "Yellow-white plaques and pseudomembranes on the colonic mucosa. Diagnostic for pseudomembranous colitis!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Clostridioides difficile Colitis" },
      { id: "wrong1", name: "Viral Gastroenteritis (Norovirus)" },
      { id: "wrong2", name: "Salmonella Gastroenteritis" },
      { id: "wrong3", name: "Inflammatory Bowel Disease Flare" },
    ],
    correctDiagnosis: "correct",
  },

  "strep-pyogenes": {
    presentation: {
      intro: "A 16-year-old presents with sudden severe sore throat, fever to 38.8°C, and difficulty swallowing. No cough.",
      clues: ["Temp 38.8°C, HR 96", "Tonsillar erythema with white exudates, uvula deviated (slightly)", "Tender anterior cervical lymphadenopathy — no cough (key distinguishing feature!)"],
    },
    tests: [
      { id: "rapid-strep", name: "Rapid Strep Antigen Test", icon: "🧪", clue: "POSITIVE — Group A Strep antigen detected. Specificity >95%, very reliable when positive." },
      { id: "throat-cx", name: "Throat Culture", icon: "🧫", clue: "Heavy growth of large beta-haemolytic colonies. Bacitracin sensitive — Group A confirmed." },
      { id: "monospot", name: "Monospot Test (EBV)", icon: "🧪", clue: "NEGATIVE — EBV mononucleosis excluded. Important because amoxicillin causes rash in mono!" },
      { id: "aso", name: "ASO Titer (Anti-Streptolysin O)", icon: "🧪", clue: "Elevated at 480 IU/mL — indicates recent streptococcal infection." },
      { id: "cbc-strep", name: "CBC", icon: "🩸", clue: "WBC 14,800 with neutrophilia. No atypical lymphocytes (which would suggest EBV)." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Group A Streptococcus — Strep Pharyngitis" },
      { id: "wrong1", name: "EBV — Infectious Mononucleosis" },
      { id: "wrong2", name: "Viral Pharyngitis (Rhinovirus/Adenovirus)" },
      { id: "wrong3", name: "Peritonsillar Abscess" },
    ],
    correctDiagnosis: "correct",
  },

  "mssa": {
    presentation: {
      intro: "A 28-year-old competitive swimmer presents with a rapidly spreading, warm, tender area of skin redness on the forearm after a minor cut 3 days ago. Fever 38.4°C.",
      clues: ["Temp 38.4°C, HR 94", "10cm area of erythema with central golden-crusted pustule", "Tender axillary lymphadenopathy"],
    },
    tests: [
      { id: "wound-gram", name: "Wound Swab Gram Stain", icon: "🔬", clue: "Gram-positive cocci in clusters — Staphylococcal morphology." },
      { id: "wound-cx-mssa", name: "Wound Swab Culture", icon: "🧫", clue: "Heavy growth of S. aureus. Catalase positive, coagulase positive." },
      { id: "oxacillin-mssa", name: "Oxacillin Sensitivity Testing", icon: "🔬", clue: "Zone of inhibition 28mm — SENSITIVE to oxacillin. This is MSSA, not MRSA!" },
      { id: "meca-neg", name: "mecA Gene PCR", icon: "🧬", clue: "mecA gene NOT DETECTED — confirms methicillin sensitivity. MSSA confirmed." },
      { id: "coagulase", name: "Coagulase Test", icon: "🧪", clue: "POSITIVE — confirms Staphylococcus aureus (distinguishes from coagulase-negative Staph like S. epidermidis)." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "MSSA (Methicillin-Sensitive S. aureus)" },
      { id: "wrong1", name: "MRSA (Methicillin-Resistant S. aureus)" },
      { id: "wrong2", name: "Group A Streptococcus — Cellulitis" },
      { id: "wrong3", name: "Erysipelas (Superficial Strep)" },
    ],
    correctDiagnosis: "correct",
  },

  "h-pylori": {
    presentation: {
      intro: "A 42-year-old office worker with 3 months of burning epigastric pain, worse when hungry and at night, slightly relieved by food and antacids. Occasional nausea.",
      clues: ["Afebrile, otherwise well", "Epigastric tenderness on deep palpation", "No alarm features (weight loss, dysphagia, or haematemesis)"],
    },
    tests: [
      { id: "ubt", name: "Urea Breath Test (UBT)", icon: "💨", clue: "POSITIVE — labelled CO2 detected in exhaled breath. H. pylori urease converts ingested urea. Non-invasive gold standard!" },
      { id: "stool-ag", name: "Stool H. pylori Antigen Test", icon: "🧪", clue: "POSITIVE — H. pylori antigen detected in stool. Sensitive and cheap non-invasive test." },
      { id: "endoscopy", name: "Upper GI Endoscopy + Biopsy", icon: "🔭", clue: "Gastric antral ulcer 1.2cm. CLO (urease) test on biopsy: POSITIVE within 2 hours. H. pylori confirmed!" },
      { id: "hp-serology", name: "H. pylori Serology (IgG)", icon: "🧪", clue: "IgG POSITIVE — but note: stays positive after eradication, so not useful to confirm cure!" },
      { id: "giemsa", name: "Histology (Giemsa Stain)", icon: "🔬", clue: "Spiral-shaped bacteria seen in gastric mucosa, nestled between epithelial cells. H. pylori confirmed." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Helicobacter pylori — Peptic Ulcer Disease" },
      { id: "wrong1", name: "NSAID-Induced Gastric Ulcer" },
      { id: "wrong2", name: "Gastric Adenocarcinoma" },
      { id: "wrong3", name: "Functional Dyspepsia (no organic cause)" },
    ],
    correctDiagnosis: "correct",
  },

  "hsv": {
    presentation: {
      intro: "A 25-year-old presents with 3 days of painful genital sores, dysuria, and swollen groin. Also reports fever and feeling unwell. This is a first episode.",
      clues: ["Temp 38.0°C, HR 88", "Multiple shallow painful ulcers on genitalia + perianal area", "Bilateral tender inguinal lymphadenopathy"],
    },
    tests: [
      { id: "lesion-pcr", name: "PCR of Lesion Swab", icon: "🧬", clue: "HSV-2 DNA detected — most sensitive test for genital herpes. Confirms primary HSV-2 infection." },
      { id: "tzanck", name: "Tzanck Smear", icon: "🔬", clue: "Multinucleated giant cells with intranuclear inclusions seen. Pathognomonic for herpesviruses!" },
      { id: "hsv-cx", name: "Viral Culture (Vesicle Fluid)", icon: "🧫", clue: "Characteristic cytopathic effect (CPE) in cell culture at 48 hours — HSV growing." },
      { id: "hsv-igm", name: "HSV Serology (IgM)", icon: "🧪", clue: "IgM POSITIVE, IgG negative — consistent with PRIMARY HSV infection (no prior immunity)." },
      { id: "darkfield-hsv", name: "Darkfield Microscopy", icon: "🔬", clue: "NEGATIVE — no spirochetes seen. Syphilis (Treponema pallidum) excluded as cause of ulcer." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Herpes Simplex Virus (HSV-2) — Primary Genital Herpes" },
      { id: "wrong1", name: "Treponema pallidum — Primary Syphilis" },
      { id: "wrong2", name: "Haemophilus ducreyi — Chancroid" },
      { id: "wrong3", name: "Molluscum Contagiosum" },
    ],
    correctDiagnosis: "correct",
  },

  "sars-cov2": {
    presentation: {
      intro: "A 60-year-old with type 2 diabetes presents with 5 days of fever, dry cough, and progressive shortness of breath. SpO2 88% on room air.",
      clues: ["Temp 38.7°C, RR 26, SpO2 88% on air → 94% on 4L O2", "Bilateral coarse crackles on auscultation", "Loss of smell and taste reported (anosmia)"],
    },
    tests: [
      { id: "np-pcr-covid", name: "Nasopharyngeal RT-PCR", icon: "🧬", clue: "SARS-CoV-2 RNA DETECTED. Cycle threshold (Ct) value 18 — high viral load." },
      { id: "rapid-ag", name: "Rapid Antigen Test", icon: "🧪", clue: "POSITIVE for SARS-CoV-2. High sensitivity when symptomatic with high viral load." },
      { id: "ct-chest", name: "CT Chest (HRCT)", icon: "🩻", clue: "Bilateral peripheral ground-glass opacities in lower lobes. 'Crazy paving' pattern. Classic COVID-19!" },
      { id: "cbc-covid", name: "CBC + Inflammatory Markers", icon: "🩸", clue: "Lymphopenia 0.6 × 10⁹/L (key feature!). CRP 145, ferritin 1,850, D-dimer 3.2 — cytokine storm brewing." },
      { id: "flu-test", name: "Influenza PCR (Co-testing)", icon: "🧪", clue: "Influenza A and B NEGATIVE. Respiratory panel negative for other viruses." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "SARS-CoV-2 — COVID-19" },
      { id: "wrong1", name: "Influenza A — Viral Pneumonia" },
      { id: "wrong2", name: "Streptococcus pneumoniae — Bacterial Pneumonia" },
      { id: "wrong3", name: "Pneumocystis jirovecii (PCP) Pneumonia" },
    ],
    correctDiagnosis: "correct",
  },

  "neisseria-gonorrhoeae": {
    presentation: {
      intro: "A 22-year-old man presents with 5 days of thick yellow-green urethral discharge and painful urination. One new sexual partner 2 weeks ago.",
      clues: ["Afebrile, otherwise well", "Profuse purulent urethral discharge visible", "Mild urethral meatal erythema"],
    },
    tests: [
      { id: "urethral-gram", name: "Urethral Gram Stain", icon: "🔬", clue: "Intracellular gram-negative diplococci inside neutrophils — classic gonococcal morphology. 95% sensitive in symptomatic males!" },
      { id: "naat-gc", name: "NAAT (Nucleic Acid Amplification)", icon: "🧬", clue: "N. gonorrhoeae POSITIVE. Most sensitive test — detects even asymptomatic infections." },
      { id: "gc-culture", name: "Urethral Culture (Thayer-Martin)", icon: "🧫", clue: "Oxidase-positive colonies growing on selective Thayer-Martin agar — N. gonorrhoeae confirmed." },
      { id: "chlamydia-cotest", name: "Chlamydia NAAT (Co-testing)", icon: "🧬", clue: "Chlamydia trachomatis also POSITIVE — 30-40% co-infection rate! Always test and treat for both." },
      { id: "rpr-gc", name: "RPR (Syphilis Screen)", icon: "🧪", clue: "Non-reactive. Syphilis excluded. Always screen for multiple STIs simultaneously." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Neisseria gonorrhoeae — Gonococcal Urethritis" },
      { id: "wrong1", name: "Chlamydia trachomatis — Non-gonococcal Urethritis" },
      { id: "wrong2", name: "Trichomonas vaginalis — Trichomoniasis" },
      { id: "wrong3", name: "HSV-2 — Herpetic Urethritis" },
    ],
    correctDiagnosis: "correct",
  },

  "h-influenzae": {
    presentation: {
      intro: "A 3-year-old girl brought in by parents with 2 days of right ear pain, fever, and unusual irritability. Pulling at right ear.",
      clues: ["Temp 38.7°C, HR 120 (child)", "Otoscopy: right tympanic membrane bulging, opaque, erythematous, loss of light reflex", "Left TM normal"],
    },
    tests: [
      { id: "tympanocentesis", name: "Tympanocentesis + Culture", icon: "🧫", clue: "Middle ear fluid growing small gram-negative coccobacilli — H. influenzae morphology." },
      { id: "beta-lac-test", name: "Beta-Lactamase Test", icon: "🧪", clue: "POSITIVE — this strain produces beta-lactamase. Plain amoxicillin will be destroyed!" },
      { id: "ear-pcr", name: "Middle Ear Fluid PCR", icon: "🧬", clue: "H. influenzae non-typeable (NTHi) confirmed — most common otitis media strain." },
      { id: "cxr-hi", name: "Chest X-Ray", icon: "🩻", clue: "Clear lung fields — no pneumonia. Isolated otitis media." },
      { id: "cbc-hi", name: "CBC", icon: "🩸", clue: "WBC 16,200 with neutrophilia. Mild systemic response to ear infection." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Haemophilus influenzae — Acute Otitis Media" },
      { id: "wrong1", name: "Streptococcus pneumoniae — Otitis Media" },
      { id: "wrong2", name: "Viral Otitis Media" },
      { id: "wrong3", name: "Malignant Otitis Externa (Pseudomonas)" },
    ],
    correctDiagnosis: "correct",
  },

  "chlamydia": {
    presentation: {
      intro: "A 19-year-old woman attends routine sexual health screening. She has no symptoms but has had 2 new partners in the past 6 months without consistent condom use.",
      clues: ["Afebrile, no symptoms (most Chlamydia infections are asymptomatic!)", "Cervical ectropion on speculum exam", "Slight cervical discharge noted"],
    },
    tests: [
      { id: "naat-ct", name: "NAAT — First Void Urine / Swab", icon: "🧬", clue: "Chlamydia trachomatis POSITIVE. NAAT is the gold standard — detects even trace amounts of DNA." },
      { id: "gram-ct", name: "Cervical Gram Stain", icon: "🔬", clue: "No intracellular diplococci seen — gonococcal cervicitis excluded. Note: Chlamydia has no cell wall, so doesn't Gram stain!" },
      { id: "ct-culture", name: "Culture in McCoy Cells", icon: "🧫", clue: "Intracellular inclusions visible in cell culture. Chlamydia confirmed. (Culture rarely done now — NAAT preferred.)" },
      { id: "ct-serology", name: "Chlamydia IgG Serology", icon: "🧪", clue: "IgG positive — indicates prior or current infection. Not used for acute diagnosis." },
      { id: "gc-cotest", name: "Gonorrhoea Co-Testing (NAAT)", icon: "🧬", clue: "N. gonorrhoeae NEGATIVE. Single pathogen infection. Always co-test for gonorrhoea!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Chlamydia trachomatis — Chlamydial Infection" },
      { id: "wrong1", name: "Neisseria gonorrhoeae — Gonorrhoea" },
      { id: "wrong2", name: "Mycoplasma genitalium" },
      { id: "wrong3", name: "Trichomonas vaginalis" },
    ],
    correctDiagnosis: "correct",
  },

  "treponema": {
    presentation: {
      intro: "A 28-year-old man presents with a painless ulcer on the penis that appeared 3 weeks ago. He noticed swollen groin glands but ignored it until now.",
      clues: ["Afebrile, otherwise well", "Single 1.5cm indurated, painless ulcer (chancre) on the glans", "Non-tender bilateral inguinal lymphadenopathy"],
    },
    tests: [
      { id: "darkfield", name: "Darkfield Microscopy (Ulcer Scraping)", icon: "🔬", clue: "Corkscrew-shaped, motile spirochetes visible! T. pallidum cannot be Gram-stained or cultured." },
      { id: "rpr", name: "RPR (Rapid Plasma Reagin)", icon: "🧪", clue: "REACTIVE at 1:16 titre. Non-treponemal test — used for screening and monitoring treatment response." },
      { id: "tppa", name: "TPPA (Confirmatory Treponemal Test)", icon: "🧪", clue: "POSITIVE — confirms treponemal infection. Stays positive for life even after treatment." },
      { id: "fta-abs", name: "FTA-ABS (Confirmatory)", icon: "🧪", clue: "POSITIVE — most sensitive confirmatory test for syphilis. Diagnosis confirmed." },
      { id: "hiv-test", name: "HIV Test", icon: "🧬", clue: "HIV NEGATIVE. Important: syphilis and HIV frequently co-occur; always co-test!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Treponema pallidum — Primary Syphilis" },
      { id: "wrong1", name: "HSV-2 — Genital Herpes" },
      { id: "wrong2", name: "Haemophilus ducreyi — Chancroid" },
      { id: "wrong3", name: "Lymphogranuloma Venereum (LGV)" },
    ],
    correctDiagnosis: "correct",
  },

  "borrelia": {
    presentation: {
      intro: "A 35-year-old who went hiking in Connecticut 2 weeks ago presents with an expanding red rash on the thigh, mild fever, fatigue, and joint aches.",
      clues: ["Temp 37.9°C, mild malaise", "8cm expanding annular rash with central clearing — 'bull's-eye' pattern", "Mild knee swelling and PR prolongation on ECG"],
    },
    tests: [
      { id: "bb-elisa", name: "Lyme ELISA Serology (Screening)", icon: "🧪", clue: "POSITIVE — sensitive screening test. Must be confirmed with Western blot (two-tier testing)." },
      { id: "western-blot", name: "Lyme Western Blot (Confirmatory)", icon: "🔬", clue: "IgM and IgG bands POSITIVE — meets CDC criteria. B. burgdorferi infection confirmed." },
      { id: "ecg-bb", name: "ECG", icon: "🫀", clue: "First-degree AV block (PR 240ms). Lyme carditis! Needs IV ceftriaxone if high-degree block." },
      { id: "skin-pcr", name: "Skin Biopsy PCR (EM Lesion)", icon: "🧬", clue: "B. burgdorferi DNA detected in biopsy of erythema migrans lesion — highest early sensitivity." },
      { id: "tick-id", name: "Tick Identification", icon: "🕷️", clue: "Patient finds an embedded Ixodes scapularis (black-legged deer tick) — the Lyme vector in NE USA." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Borrelia burgdorferi — Lyme Disease" },
      { id: "wrong1", name: "Rocky Mountain Spotted Fever (Rickettsia)" },
      { id: "wrong2", name: "Streptococcal Cellulitis" },
      { id: "wrong3", name: "Erythema Multiforme" },
    ],
    correctDiagnosis: "correct",
  },

  "n-meningitidis": {
    presentation: {
      intro: "A 19-year-old college student is brought in by roommates. He developed sudden severe headache 6 hours ago, now has neck stiffness, fever, and a spreading non-blanching rash.",
      clues: ["Temp 39.9°C, HR 128, BP 88/54 (shock!)", "Neck stiffness — positive Kernig's and Brudzinski's signs", "Non-blanching petechiae spreading to purpura — THIS IS AN EMERGENCY"],
    },
    tests: [
      { id: "lp-nm", name: "Lumbar Puncture — CSF Analysis", icon: "💉", clue: "Turbid CSF. WBC 18,000 (90% neutrophils), glucose 1.2 (low!), protein 4.2 (high). Bacterial meningitis pattern!" },
      { id: "csf-gram-nm", name: "CSF Gram Stain", icon: "🔬", clue: "Gram-negative diplococci inside neutrophils — pathognomonic for N. meningitidis!" },
      { id: "blood-cx-nm", name: "Blood Culture (×2)", icon: "🧫", clue: "N. meningitidis growing within 12 hours. Do NOT delay antibiotics waiting for cultures!" },
      { id: "pcr-nm", name: "Meningococcal PCR (CSF/Blood)", icon: "🧬", clue: "N. meningitidis serogroup B confirmed. PCR works even after antibiotics have been started." },
      { id: "ct-nm", name: "CT Head", icon: "🩻", clue: "No mass lesion or raised ICP. Safe to proceed with LP. Do NOT delay LP (or antibiotics) for CT unless focal neurology!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Neisseria meningitidis — Meningococcal Meningitis + Septicaemia" },
      { id: "wrong1", name: "Streptococcus pneumoniae — Pneumococcal Meningitis" },
      { id: "wrong2", name: "HSV — Viral Encephalitis" },
      { id: "wrong3", name: "Subarachnoid Haemorrhage" },
    ],
    correctDiagnosis: "correct",
  },

  "cryptococcus": {
    presentation: {
      intro: "A 42-year-old HIV-positive man (CD4 count 45) presents with 2 weeks of progressively worsening headache, photophobia, and new confusion.",
      clues: ["Temp 38.3°C, GCS 13/15", "Neck stiffness, papilloedema on fundoscopy", "CD4 count 45 — severe immunosuppression (key clue!)"],
    },
    tests: [
      { id: "lp-crypto", name: "Lumbar Puncture + Opening Pressure", icon: "💉", clue: "Opening pressure 40 cmH2O (markedly elevated — normal <20). Crystal-clear CSF. Lymphocytic pleocytosis." },
      { id: "india-ink", name: "India Ink Stain (CSF)", icon: "🔬", clue: "Encapsulated yeast with a clear halo — the capsule repels the ink. Pathognomonic for Cryptococcus!" },
      { id: "crag", name: "Cryptococcal Antigen (CrAg) — CSF + Serum", icon: "🧪", clue: "CrAg titre >1:1024 in both CSF and serum. Sensitivity >99% — most useful test." },
      { id: "blood-cx-crypto", name: "Blood Culture", icon: "🧫", clue: "Encapsulated yeast growing — cryptococcaemia confirmed. Poor prognostic sign." },
      { id: "mri-crypto", name: "MRI Brain with Contrast", icon: "🩻", clue: "No ring-enhancing lesions. Toxoplasma encephalitis (which causes rings) less likely. Dilated Virchow-Robin spaces." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Cryptococcus neoformans — Cryptococcal Meningitis" },
      { id: "wrong1", name: "Toxoplasma gondii — Cerebral Toxoplasmosis" },
      { id: "wrong2", name: "Mycobacterium tuberculosis — TB Meningitis" },
      { id: "wrong3", name: "CMV — CMV Encephalitis" },
    ],
    correctDiagnosis: "correct",
  },

  "mucor": {
    presentation: {
      intro: "A 55-year-old with uncontrolled type 1 diabetes (glucose 31 mmol/L, pH 7.09 — DKA) presents with 3 days of facial pain, black nasal discharge, and proptosis of the left eye.",
      clues: ["Diabetic ketoacidosis — key risk factor!", "Left orbital proptosis and ophthalmoplegia", "Black eschar visible in left nasal cavity — tissue necrosis!"],
    },
    tests: [
      { id: "ct-sinus", name: "CT Sinuses + Orbit", icon: "🩻", clue: "Extensive left sinus destruction with bony erosion. Orbital involvement and perineural spread visible." },
      { id: "biopsy-mucor", name: "Tissue Biopsy (Nasal Eschar)", icon: "🔬", clue: "Wide, ribbon-like, non-septate (aseptate) hyphae branching at right angles. Pathognomonic for Mucorales!" },
      { id: "koh-mucor", name: "KOH Preparation (Tissue)", icon: "🔬", clue: "Aseptate hyphae with irregular wide ribbons visible. Branching at near 90° — distinguishes from Aspergillus (45°)." },
      { id: "glucose-mucor", name: "Blood Glucose + ABG", icon: "🩸", clue: "Glucose 31 mmol/L, pH 7.09, ketones +++. DKA creates the perfect environment for Mucor (loves acidic, high-glucose conditions)." },
      { id: "mri-mucor", name: "MRI Orbit + Brain", icon: "🩻", clue: "Cavernous sinus thrombosis developing. Intracranial extension — this is now rhinocerebral mucormycosis." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Mucormycosis (Rhizopus/Mucor) — Rhinocerebral" },
      { id: "wrong1", name: "Aspergillus fumigatus — Invasive Sinusitis" },
      { id: "wrong2", name: "Bacterial Sinusitis (S. aureus)" },
      { id: "wrong3", name: "Orbital Cellulitis (Preseptal)" },
    ],
    correctDiagnosis: "correct",
  },

  "vre": {
    presentation: {
      intro: "A 68-year-old 3 weeks post-liver transplant, on tacrolimus, has persistent fever and bacteraemia despite 5 days of IV vancomycin.",
      clues: ["Temp 38.5°C persisting on vancomycin (red flag!)", "Central line in situ, recent abdominal surgery", "Jaundice worsening — biliary source suspected"],
    },
    tests: [
      { id: "blood-cx-vre", name: "Blood Culture — Repeat", icon: "🧫", clue: "Enterococcus species growing. Gram-positive cocci in pairs and short chains." },
      { id: "vanc-mic", name: "Vancomycin MIC Testing", icon: "🔬", clue: "Vancomycin MIC >256 μg/mL — highly resistant. Standard vancomycin cannot possibly work at this level!" },
      { id: "vana-pcr", name: "vanA Gene PCR", icon: "🧬", clue: "vanA gene DETECTED — the resistance gene that remodels the cell wall D-Ala-D-Ala target. Vancomycin slides off!" },
      { id: "sensitivity", name: "Full Antibiotic Sensitivity Panel", icon: "🧪", clue: "Resistant: vancomycin, ampicillin, penicillin. Sensitive: linezolid, daptomycin. Clear treatment path!" },
      { id: "bile-cx", name: "Bile Culture (T-Tube Drain)", icon: "🧫", clue: "VRE isolated from bile — biliary source of bacteraemia identified. Source control needed." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Vancomycin-Resistant Enterococcus (VRE) — Bacteraemia" },
      { id: "wrong1", name: "MRSA — Staphylococcal Bacteraemia" },
      { id: "wrong2", name: "Candida albicans — Candidaemia" },
      { id: "wrong3", name: "E. coli — Gram-Negative Bacteraemia" },
    ],
    correctDiagnosis: "correct",
  },

  "toxoplasma": {
    presentation: {
      intro: "A 38-year-old HIV-positive man (CD4 80, not on ART) presents with 1 week of progressive headache, confusion, and new right-sided weakness.",
      clues: ["Temp 37.8°C, GCS 12/15", "Right hemiparesis and right facial droop", "CD4 count 80 — at high risk for opportunistic infections"],
    },
    tests: [
      { id: "mri-toxo", name: "MRI Brain with Gadolinium", icon: "🩻", clue: "Multiple ring-enhancing lesions in the basal ganglia and corticomedullary junction — classic toxoplasma distribution!" },
      { id: "toxo-igg", name: "Toxoplasma IgG Serology", icon: "🧪", clue: "IgG POSITIVE (high titre) — confirms prior exposure. Reactivation of latent cysts is the mechanism in AIDS." },
      { id: "csf-pcr-toxo", name: "CSF PCR — Toxoplasma gondii", icon: "🧬", clue: "T. gondii DNA DETECTED in CSF. Confirms active CNS toxoplasmosis." },
      { id: "cd4-toxo", name: "CD4 Count", icon: "🩸", clue: "CD4 80 cells/μL — below 100 is the threshold where toxoplasma reactivation occurs." },
      { id: "empiric-trial", name: "Empiric Anti-Toxoplasma Trial", icon: "💊", clue: "Clinical and radiological improvement after 2 weeks of pyrimethamine-sulfadiazine confirms toxoplasma (vs lymphoma which would not respond)." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Toxoplasma gondii — Cerebral Toxoplasmosis" },
      { id: "wrong1", name: "CNS Lymphoma (Primary)" },
      { id: "wrong2", name: "Cryptococcus neoformans — Cryptococcal Meningitis" },
      { id: "wrong3", name: "Mycobacterium tuberculosis — TB Meningitis" },
    ],
    correctDiagnosis: "correct",
  },

  "pseudomonas": {
    presentation: {
      intro: "A 70-year-old ventilated ICU patient (day 8 of mechanical ventilation) develops fever, increased purulent secretions, and worsening oxygenation.",
      clues: ["Temp 38.8°C, FiO2 requirement up from 40% to 65%", "New infiltrate on CXR, purulent green endotracheal secretions", "Multiple prior antibiotic courses in this admission"],
    },
    tests: [
      { id: "et-gram", name: "Endotracheal Aspirate Gram Stain", icon: "🔬", clue: "Gram-negative rods — non-fermentative morphology. Pseudomonas is the most common gram-negative VAP pathogen." },
      { id: "bal-cx", name: "Bronchoalveolar Lavage (BAL) Culture", icon: "🧫", clue: "Heavy growth of non-fermentative gram-negative rods with fruity grape-like odour (pyocyanin pigment). Pseudomonas aeruginosa." },
      { id: "sensitivity-pseudo", name: "Antibiotic Sensitivity Panel", icon: "🧪", clue: "Resistant to amoxicillin, 1st/2nd gen cephalosporins, vancomycin. Sensitive to pip-tazo, meropenem, ceftazidime." },
      { id: "procalcitonin", name: "Procalcitonin", icon: "🩸", clue: "Procalcitonin 18 ng/mL (markedly elevated). Strong evidence of bacterial rather than viral cause." },
      { id: "cxr-pseudo", name: "Chest X-Ray", icon: "🩻", clue: "New left lower lobe infiltrate consistent with VAP. Bilateral infiltrates would raise ARDS concern." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Pseudomonas aeruginosa — Ventilator-Associated Pneumonia" },
      { id: "wrong1", name: "Streptococcus pneumoniae — Hospital-Acquired Pneumonia" },
      { id: "wrong2", name: "MRSA — Staphylococcal Pneumonia" },
      { id: "wrong3", name: "Klebsiella pneumoniae — Gram-Negative Pneumonia" },
    ],
    correctDiagnosis: "correct",
  },

  "aspergillus": {
    presentation: {
      intro: "A 45-year-old post-allogeneic stem cell transplant (day 22) develops persistent fever, pleuritic chest pain, and haemoptysis despite broad-spectrum antibiotics.",
      clues: ["Temp 38.6°C, SpO2 94%", "Neutropenia (neutrophils 0.1 × 10⁹/L — profound)", "Pleuritic rub audible at right base"],
    },
    tests: [
      { id: "ct-asp", name: "CT Chest (High Resolution)", icon: "🩻", clue: "'Halo sign' — nodule surrounded by ground-glass opacity (haemorrhage around fungal nodule). Classic for angioinvasive aspergillosis!" },
      { id: "galactomannan-serum", name: "Serum Galactomannan", icon: "🧪", clue: "Galactomannan index 3.8 (>0.5 is positive). Cell wall polysaccharide of Aspergillus — highly specific marker." },
      { id: "galactomannan-bal", name: "BAL Galactomannan", icon: "🧪", clue: "BAL galactomannan index 4.2 — even more sensitive than serum. Strong diagnostic evidence." },
      { id: "biopsy-asp", name: "Transbronchial Biopsy", icon: "🔬", clue: "Septate hyphae branching at 45° angles — pathognomonic for Aspergillus. (Compare: Mucor = non-septate, 90° branching.)" },
      { id: "beta-glucan-asp", name: "Beta-D-Glucan", icon: "🧪", clue: "Markedly elevated — present in both Aspergillus and Candida cell walls. Non-specific but supportive." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Aspergillus fumigatus — Invasive Pulmonary Aspergillosis" },
      { id: "wrong1", name: "Mucormycosis (Mucor/Rhizopus)" },
      { id: "wrong2", name: "Cryptococcus neoformans — Pulmonary Cryptococcosis" },
      { id: "wrong3", name: "Pneumocystis jirovecii (PCP) Pneumonia" },
    ],
    correctDiagnosis: "correct",
  },

  "hiv": {
    presentation: {
      intro: "A 26-year-old presents with 2 weeks of fever, sore throat, swollen glands all over, and a maculopapular rash on the trunk. He reports a new sexual partner 4 weeks ago.",
      clues: ["Temp 38.5°C, widespread lymphadenopathy", "Maculopapular rash on trunk and arms — oral ulcers", "Symptoms started ~4 weeks after potential exposure"],
    },
    tests: [
      { id: "hiv-4thgen", name: "HIV 4th Generation Ag/Ab Test", icon: "🧪", clue: "REACTIVE — detects both HIV p24 antigen AND antibodies. Positive within 2-4 weeks of infection. Gold standard screening!" },
      { id: "hiv-vl", name: "HIV RNA Viral Load (PCR)", icon: "🧬", clue: "HIV RNA >1,000,000 copies/mL — very high viraemia typical of acute HIV. Confirms active infection." },
      { id: "cd4-hiv", name: "CD4 Count", icon: "🩸", clue: "CD4 count 420 — transiently low during acute phase. Will partially recover with ART." },
      { id: "western-blot-hiv", name: "HIV Western Blot (Confirmatory)", icon: "🔬", clue: "POSITIVE with multiple HIV antibody bands — confirms HIV-1 infection." },
      { id: "monospot-hiv", name: "Monospot Test (EBV)", icon: "🧪", clue: "NEGATIVE — EBV mononucleosis excluded. Both can cause identical presentations! Always test for HIV in 'mono-like' illness." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "HIV-1 — Acute Seroconversion Illness" },
      { id: "wrong1", name: "EBV — Infectious Mononucleosis" },
      { id: "wrong2", name: "CMV — Primary CMV Infection" },
      { id: "wrong3", name: "Secondary Syphilis" },
    ],
    correctDiagnosis: "correct",
  },
  // ── EVEN MORE EASY ─────────────────────────────────────────────────────────
  "s-saprophyticus": {
    presentation: {
      intro: "A 22-year-old sexually active woman presents with 3 days of dysuria, urinary frequency, and suprapubic discomfort. No fever or flank pain.",
      clues: ["Afebrile, well-appearing", "Suprapubic tenderness", "Symptoms began shortly after sexual activity — classic history!"],
    },
    tests: [
      { id: "dipstick-ss", name: "Urine Dipstick", icon: "🧪", clue: "Leukocyte esterase POSITIVE. Nitrites NEGATIVE — key distinguishing feature! (Unlike E. coli, S. saprophyticus doesn't reduce nitrates.)" },
      { id: "micro-ss", name: "Urine Microscopy", icon: "🔬", clue: ">50 WBCs/hpf. Gram-positive cocci in clusters visible. No gram-negative rods." },
      { id: "culture-ss", name: "Urine Culture", icon: "🧫", clue: ">100,000 CFU/mL of gram-positive cocci. Catalase positive, coagulase negative. Novobiocin RESISTANT — S. saprophyticus!" },
      { id: "novobiocin", name: "Novobiocin Disk Test", icon: "🔬", clue: "Zone of inhibition <10mm — RESISTANT to novobiocin. This single test distinguishes S. saprophyticus from S. epidermidis (sensitive)." },
      { id: "koh-ss", name: "KOH Preparation", icon: "🔬", clue: "NEGATIVE — no yeast or hyphae. Candida UTI excluded." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Staphylococcus saprophyticus — UTI" },
      { id: "wrong1", name: "E. coli — Uncomplicated UTI" },
      { id: "wrong2", name: "Candida albicans — Fungal UTI" },
      { id: "wrong3", name: "Chlamydia trachomatis — Cervicitis" },
    ],
    correctDiagnosis: "correct",
  },

  "bordetella": {
    presentation: {
      intro: "A 6-month-old infant presents with 2 weeks of worsening cough. Parents describe violent coughing fits followed by a gasping 'whoop' sound, sometimes ending in vomiting. Afebrile.",
      clues: ["Afebrile (fever is NOT typical)", "Paroxysmal coughing fits with post-tussive vomiting", "Inspiratory whoop audible — unvaccinated infant"],
    },
    tests: [
      { id: "pcr-bp", name: "Nasopharyngeal PCR (B. pertussis)", icon: "🧬", clue: "B. pertussis DNA DETECTED — most sensitive test, gold standard in the first 3 weeks." },
      { id: "culture-bp", name: "Nasopharyngeal Culture (Bordet-Gengou agar)", icon: "🧫", clue: "Small colonies with 'mercury droplet' appearance growing on Bordet-Gengou agar. B. pertussis confirmed." },
      { id: "cbc-bp", name: "CBC", icon: "🩸", clue: "WBC 38,000 with marked LYMPHOCYTOSIS — classic for pertussis! (Pertussis toxin causes lymphocytes to accumulate in blood.)" },
      { id: "serology-bp", name: "Pertussis Serology (IgG anti-PT)", icon: "🧪", clue: "Anti-pertussis toxin IgG elevated — useful in later disease stages when culture/PCR sensitivity falls." },
      { id: "cxr-bp", name: "Chest X-Ray", icon: "🩻", clue: "Perihilar infiltrates — 'shaggy heart sign'. No lobar consolidation. Consistent with pertussis bronchopneumonia." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Bordetella pertussis — Whooping Cough" },
      { id: "wrong1", name: "RSV — Bronchiolitis" },
      { id: "wrong2", name: "Viral Croup (Parainfluenza)" },
      { id: "wrong3", name: "Mycoplasma pneumoniae — Atypical Pneumonia" },
    ],
    correctDiagnosis: "correct",
  },

  "vzv": {
    presentation: {
      intro: "A 72-year-old presents with 2 days of severe burning pain along the right side of the chest, followed today by a unilateral blistering rash in a band-like distribution.",
      clues: ["Unilateral vesicular rash in a dermatomal distribution — does NOT cross the midline", "Severe neuropathic pain preceding the rash by 2-3 days", "History of chickenpox as a child — VZV reactivation!"],
    },
    tests: [
      { id: "pcr-vzv", name: "PCR of Vesicle Fluid", icon: "🧬", clue: "VZV DNA DETECTED — most sensitive and specific test for active VZV infection." },
      { id: "tzanck-vzv", name: "Tzanck Smear", icon: "🔬", clue: "Multinucleated giant cells seen — consistent with herpesvirus infection (does not distinguish VZV from HSV)." },
      { id: "vzv-igm", name: "VZV IgM Serology", icon: "🧪", clue: "VZV IgM POSITIVE — acute infection/reactivation confirmed. IgG also positive (prior exposure)." },
      { id: "culture-vzv", name: "Viral Culture", icon: "🧫", clue: "VZV growing — slow-growing cytopathic effect in cell culture. PCR is faster and preferred." },
      { id: "hiv-vzv", name: "HIV Test", icon: "🧪", clue: "HIV NEGATIVE. VZV reactivation in a <50 year-old should prompt immune workup — in elderly it's common without immune deficiency." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Varicella-Zoster Virus — Herpes Zoster (Shingles)" },
      { id: "wrong1", name: "HSV-2 — Genital/Truncal Herpes" },
      { id: "wrong2", name: "Contact Dermatitis" },
      { id: "wrong3", name: "Impetigo (S. aureus / Group A Strep)" },
    ],
    correctDiagnosis: "correct",
  },

  "gbs": {
    presentation: {
      intro: "A 2-day-old neonate born at 38 weeks becomes lethargic, refuses feeds, and develops a temperature of 38.3°C. Mother had no antenatal GBS screening.",
      clues: ["Neonate — day of life 2 (early-onset sepsis window: 0-7 days)", "Fever, lethargy, poor feeding — non-specific but alarming in a neonate", "Mother GBS-screening status unknown — key risk factor!"],
    },
    tests: [
      { id: "blood-cx-gbs", name: "Neonatal Blood Culture", icon: "🧫", clue: "Gram-positive cocci in chains growing — Group B Streptococcus isolated!" },
      { id: "csf-gbs", name: "Lumbar Puncture (CSF Analysis)", icon: "💉", clue: "CSF: WBC 850 (neutrophils), protein elevated, glucose low — bacterial meningitis pattern. GBS on CSF culture." },
      { id: "cbc-gbs", name: "Neonatal CBC", icon: "🩸", clue: "WBC 3,200 (neutropenia!) with band forms. Thrombocytopenia. CRP 85. Early-onset sepsis pattern." },
      { id: "maternal-swab", name: "Maternal GBS Swab (Retrospective)", icon: "🧪", clue: "Maternal recto-vaginal swab POSITIVE for GBS — confirms maternal colonisation as source." },
      { id: "cxr-gbs", name: "Neonatal Chest X-Ray", icon: "🩻", clue: "Diffuse granular opacities — GBS pneumonia. Nearly identical to surfactant deficiency (RDS) on X-ray." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Streptococcus agalactiae (GBS) — Neonatal Early-Onset Sepsis" },
      { id: "wrong1", name: "E. coli — Neonatal Sepsis" },
      { id: "wrong2", name: "Listeria monocytogenes — Neonatal Meningitis" },
      { id: "wrong3", name: "Respiratory Distress Syndrome (Non-infectious)" },
    ],
    correctDiagnosis: "correct",
  },

  "tetanus": {
    presentation: {
      intro: "A 45-year-old farmer presents with 3 days of jaw stiffness and difficulty swallowing, followed by painful muscle spasms in the back and abdomen. He cut his foot on a rusty nail 10 days ago.",
      clues: ["Trismus ('lockjaw') — cannot open mouth fully", "Risus sardonicus — sustained spastic grin from facial muscle spasm", "Puncture wound on foot — the portal of entry"],
    },
    tests: [
      { id: "clinical-tetanus", name: "Clinical Diagnosis (Spatula Test)", icon: "🩺", clue: "Touching posterior pharynx triggers jaw closure (biting the spatula) instead of gag reflex — POSITIVE spatula test. Tetanus is a CLINICAL diagnosis!" },
      { id: "wound-cx-tet", name: "Wound Culture", icon: "🧫", clue: "Anaerobic culture: gram-positive rods with terminal spores ('drumstick' appearance). C. tetani isolated." },
      { id: "vax-history", name: "Vaccination History", icon: "📋", clue: "Last tetanus booster >20 years ago — not up to date. Key risk factor! (Tetanus is almost exclusively a disease of the unvaccinated.)" },
      { id: "emg-tetanus", name: "EMG", icon: "⚡", clue: "Continuous motor unit activity at rest — characteristic of tetanospasmin blocking inhibitory neurotransmitters." },
      { id: "toxin-assay", name: "Tetanus Toxin Assay", icon: "🧪", clue: "Serum tetanus toxin DETECTABLE by ELISA — confirms tetanospasmin presence." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Clostridium tetani — Tetanus" },
      { id: "wrong1", name: "Strychnine Poisoning" },
      { id: "wrong2", name: "Meningitis (Bacterial)" },
      { id: "wrong3", name: "Dystonic Reaction (Drug-induced)" },
    ],
    correctDiagnosis: "correct",
  },

  "salmonella-nts": {
    presentation: {
      intro: "A 28-year-old presents 18 hours after a barbecue with sudden-onset nausea, vomiting, fever, and profuse watery (now bloody) diarrhoea. Several friends who ate the same chicken are also ill.",
      clues: ["Fever 38.4°C, HR 100", "Crampy abdominal pain with bloody diarrhoea", "Cluster of illness after shared meal — foodborne outbreak pattern"],
    },
    tests: [
      { id: "stool-cx-salm", name: "Stool Culture", icon: "🧫", clue: "Lactose-NON-fermenting gram-negative rods on MacConkey agar. H2S production (black colonies on XLD agar). Salmonella confirmed." },
      { id: "stool-micro-salm", name: "Stool Microscopy", icon: "🔬", clue: "WBCs present in stool — indicates invasive (inflammatory) diarrhoea, not purely secretory." },
      { id: "cbc-salm", name: "CBC", icon: "🩸", clue: "WBC 13,500 with neutrophilia. Mild. Normal platelet count. Blood cultures negative — not bacteraemic (uncomplicated)." },
      { id: "food-cx", name: "Food Culture (Chicken Sample)", icon: "🍗", clue: "Salmonella Typhimurium isolated from leftover chicken at the barbecue — source confirmed!" },
      { id: "cdiff-salm", name: "Stool C. diff PCR", icon: "🧬", clue: "NEGATIVE — C. difficile excluded. No recent antibiotic use in this patient." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Non-Typhoidal Salmonella — Gastroenteritis" },
      { id: "wrong1", name: "Campylobacter jejuni — Gastroenteritis" },
      { id: "wrong2", name: "Clostridioides difficile Colitis" },
      { id: "wrong3", name: "Norovirus — Viral Gastroenteritis" },
    ],
    correctDiagnosis: "correct",
  },

  // ── EVEN MORE MEDIUM ───────────────────────────────────────────────────────
  "salmonella-typhi": {
    presentation: {
      intro: "A 24-year-old returns from visiting family in Pakistan. For the past 10 days: rising fever, severe headache, abdominal pain, and constipation (now diarrhoea). Relative bradycardia noted.",
      clues: ["Temp 39.8°C but HR only 78 (relative bradycardia — classic for typhoid!)", "Rose-coloured spots on the abdomen", "Splenomegaly on examination"],
    },
    tests: [
      { id: "blood-cx-typhi", name: "Blood Culture (Week 1)", icon: "🧫", clue: "Lactose-non-fermenting gram-negative rods growing — H2S positive. Salmonella typhi identified. Best sensitivity in week 1!" },
      { id: "bone-cx-typhi", name: "Bone Marrow Culture", icon: "🧫", clue: "Salmonella typhi growing — most sensitive test (90%) even after antibiotics started!" },
      { id: "widal-typhi", name: "Widal Test (Serology)", icon: "🧪", clue: "O and H agglutinin titres elevated. Useful in endemic areas but poor specificity — many false positives!" },
      { id: "cbc-typhi", name: "CBC", icon: "🩸", clue: "Leukopenia (WBC 3,200) — low white count in typhoid is classic (unlike most bacterial infections where WBC is elevated)!" },
      { id: "pcr-typhi", name: "Typhoid PCR (Blood)", icon: "🧬", clue: "S. typhi DNA detected — highly specific, increasingly available in endemic regions." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Salmonella typhi — Typhoid Fever" },
      { id: "wrong1", name: "Plasmodium falciparum — Malaria" },
      { id: "wrong2", name: "Dengue Fever" },
      { id: "wrong3", name: "EBV — Infectious Mononucleosis" },
    ],
    correctDiagnosis: "correct",
  },

  "rickettsia": {
    presentation: {
      intro: "A 35-year-old hiker returns from camping in North Carolina. Over 5 days: sudden severe headache, fever to 40°C, and myalgias. Today a rash appeared on the wrists and ankles.",
      clues: ["Fever 40.1°C, HR 112, severe headache", "Maculopapular rash starting on wrists/ankles, spreading centripetally to trunk", "Recent tick exposure during camping — found a tick attached 8 days ago"],
    },
    tests: [
      { id: "serology-rmsf", name: "Rickettsia Serology (IFA)", icon: "🧪", clue: "IgM/IgG titres POSITIVE — indirect fluorescent antibody test. May be negative in first week (treat empirically, don't wait!)." },
      { id: "pcr-rmsf", name: "Whole Blood PCR (R. rickettsii)", icon: "🧬", clue: "R. rickettsii DNA DETECTED — most specific test in acute phase before antibody response develops." },
      { id: "biopsy-rmsf", name: "Skin Biopsy (Rash) — Immunofluorescence", icon: "🔬", clue: "Rickettsia antigens visible inside endothelial cells — pathognomonic finding!" },
      { id: "cbc-rmsf", name: "CBC + Metabolic Panel", icon: "🩸", clue: "Thrombocytopenia, hyponatraemia, elevated LFTs — triad highly suggestive of RMSF vasculitis." },
      { id: "tick-rmsf", name: "Tick Identification", icon: "🕷️", clue: "Dermacentor variabilis (American dog tick) — the primary RMSF vector in the eastern US." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Rickettsia rickettsii — Rocky Mountain Spotted Fever" },
      { id: "wrong1", name: "Meningococcaemia (N. meningitidis)" },
      { id: "wrong2", name: "Lyme Disease (B. burgdorferi)" },
      { id: "wrong3", name: "Ehrlichiosis / Anaplasmosis" },
    ],
    correctDiagnosis: "correct",
  },

  "listeria": {
    presentation: {
      intro: "A 68-year-old on long-term prednisolone for rheumatoid arthritis presents with 3 days of fever, severe headache, and neck stiffness. He recently ate soft cheese and deli meats.",
      clues: ["Temp 39.2°C, GCS 14/15, neck stiffness", "Immunosuppressed — steroids suppress cell-mediated immunity (key Listeria risk!)", "Dietary history: soft unpasteurised cheese — classic Listeria source"],
    },
    tests: [
      { id: "lp-listeria", name: "Lumbar Puncture — CSF Analysis", icon: "💉", clue: "Turbid CSF. WBC 2,800 (mixed lymphocytes and neutrophils — unusual!). Glucose low, protein high. Gram-positive rods on smear!" },
      { id: "csf-cx-list", name: "CSF Culture", icon: "🧫", clue: "Gram-positive rods growing — tumbling motility at room temperature (characteristic of Listeria). L. monocytogenes confirmed." },
      { id: "blood-cx-list", name: "Blood Culture", icon: "🧫", clue: "Listeria monocytogenes growing in blood — bacteraemia seeding the meninges." },
      { id: "food-list", name: "Food Culture (Soft Cheese)", icon: "🧀", clue: "L. monocytogenes isolated from the soft cheese — growth at 4°C (refrigerator temperature) confirms the source." },
      { id: "mri-list", name: "MRI Brain", icon: "🩻", clue: "Rhombencephalitis — brainstem lesions visible. Classic Listeria presentation in immunocompromised. Ring-enhancing lesions possible." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Listeria monocytogenes — Meningitis / Encephalitis" },
      { id: "wrong1", name: "Streptococcus pneumoniae — Pneumococcal Meningitis" },
      { id: "wrong2", name: "Cryptococcus neoformans — Cryptococcal Meningitis" },
      { id: "wrong3", name: "HSV — Viral Encephalitis" },
    ],
    correctDiagnosis: "correct",
  },

  "hcv": {
    presentation: {
      intro: "A 45-year-old with a history of IV drug use in his 20s is found to have elevated liver enzymes on routine blood work. He feels fatigued but otherwise well. No jaundice.",
      clues: ["ALT 85 U/L, AST 72 U/L (mildly elevated × 6 months)", "No alcohol use, no medications causing hepatotoxicity", "History of IV drug use 20 years ago — key risk factor!"],
    },
    tests: [
      { id: "hcv-ab", name: "HCV Antibody (Screening)", icon: "🧪", clue: "HCV antibody REACTIVE — confirms exposure to HCV. Needs confirmatory test (doesn't distinguish active from cleared infection)." },
      { id: "hcv-rna", name: "HCV RNA (Viral Load PCR)", icon: "🧬", clue: "HCV RNA 2,400,000 IU/mL — ACTIVE chronic infection confirmed! High viraemia." },
      { id: "hcv-geno", name: "HCV Genotype", icon: "🧬", clue: "Genotype 1a — most common in North America. Pan-genotypic DAAs work regardless, but genotyping guides some regimen choices." },
      { id: "fibroscan", name: "Fibroscan (Liver Elastography)", icon: "🫀", clue: "Liver stiffness 9.2 kPa — significant fibrosis (F2-F3). Not yet cirrhosis but treatment is urgent!" },
      { id: "hbv-hcv", name: "HBV Co-testing (HBsAg)", icon: "🧪", clue: "HBsAg NEGATIVE — HBV co-infection excluded. Always co-test, as DAAs can reactivate HBV!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Hepatitis C Virus (HCV) — Chronic Hepatitis C" },
      { id: "wrong1", name: "Hepatitis B Virus (HBV) — Chronic Hepatitis B" },
      { id: "wrong2", name: "Non-alcoholic Fatty Liver Disease (NAFLD)" },
      { id: "wrong3", name: "Autoimmune Hepatitis" },
    ],
    correctDiagnosis: "correct",
  },

  "klebsiella": {
    presentation: {
      intro: "A 58-year-old with alcohol use disorder presents with sudden fever, productive cough with thick dark-red sputum, and pleuritic chest pain. He looks acutely unwell.",
      clues: ["Temp 39.4°C, HR 115, RR 28, SpO2 91%", "Dullness and bronchial breathing right upper zone", "'Currant jelly' haemoptysis — dark reddish-brown mucoid sputum"],
    },
    tests: [
      { id: "sputum-gram-kleb", name: "Sputum Gram Stain", icon: "🔬", clue: "Large encapsulated gram-negative rods with thick mucoid capsule. Classic Klebsiella morphology." },
      { id: "sputum-cx-kleb", name: "Sputum Culture", icon: "🧫", clue: "Mucoid, string-forming 'mucoviscous' colonies — positive string test >5mm. Klebsiella pneumoniae confirmed." },
      { id: "cxr-kleb", name: "Chest X-Ray", icon: "🩻", clue: "Dense right upper lobe consolidation with 'bulging fissure sign' — classic Klebsiella finding (heavy mucoid consolidation bows the fissure downward)." },
      { id: "blood-cx-kleb", name: "Blood Culture", icon: "🧫", clue: "Klebsiella pneumoniae growing — bacteraemia present. Sensitivity testing: ESBL negative, susceptible to cephalosporins." },
      { id: "cbc-kleb", name: "CBC", icon: "🩸", clue: "WBC 22,000 with left shift. CRP 340 mg/L — severe systemic inflammation." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Klebsiella pneumoniae — Lobar Pneumonia" },
      { id: "wrong1", name: "Streptococcus pneumoniae — Pneumococcal Pneumonia" },
      { id: "wrong2", name: "Pseudomonas aeruginosa — Gram-Negative Pneumonia" },
      { id: "wrong3", name: "Staphylococcus aureus — Staphylococcal Pneumonia" },
    ],
    correctDiagnosis: "correct",
  },

  "norovirus": {
    presentation: {
      intro: "Seven patients on the same hospital ward develop sudden-onset vomiting and watery diarrhoea within 12 hours of each other. The ward is being closed to new admissions.",
      clues: ["Outbreak pattern — multiple cases simultaneously (key clue!)", "Sudden-onset projectile vomiting followed by watery non-bloody diarrhoea", "Afebrile or low-grade fever only — self-limiting illness"],
    },
    tests: [
      { id: "stool-pcr-noro", name: "Stool RT-PCR (Norovirus)", icon: "🧬", clue: "Norovirus GII RNA DETECTED — gold standard test. Highly sensitive even with small amounts of stool." },
      { id: "stool-em-noro", name: "Electron Microscopy (Stool)", icon: "🔬", clue: "Small round structured viruses (SRSVs) with characteristic 'Star of David' appearance — Norovirus morphology." },
      { id: "stool-cx-noro", name: "Stool Bacterial Culture", icon: "🧫", clue: "No bacterial pathogens grown — Salmonella, Campylobacter, and E. coli excluded. Supports viral aetiology." },
      { id: "cdiff-noro", name: "Stool C. diff PCR", icon: "🧬", clue: "NEGATIVE — C. difficile excluded. No recent antibiotics on this ward." },
      { id: "cbc-noro", name: "CBC", icon: "🩸", clue: "Normal WBC without neutrophilia — no systemic bacterial infection. Consistent with viral gastroenteritis." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Norovirus — Viral Gastroenteritis Outbreak" },
      { id: "wrong1", name: "Salmonella — Foodborne Outbreak" },
      { id: "wrong2", name: "Clostridioides difficile — Outbreak" },
      { id: "wrong3", name: "Staphylococcal Food Poisoning (S. aureus toxin)" },
    ],
    correctDiagnosis: "correct",
  },

  // ── EVEN MORE HARD ─────────────────────────────────────────────────────────
  "pcp": {
    presentation: {
      intro: "A 38-year-old HIV-positive man (CD4 count 65, not on ART or prophylaxis) presents with 3 weeks of progressive exertional dyspnoea, non-productive cough, and fever. SpO2 86% on room air.",
      clues: ["SpO2 86% on air (severe hypoxaemia — out of proportion to X-ray findings!)", "Dry non-productive cough — no sputum", "CD4 count 65 — below the 200 threshold for PCP prophylaxis"],
    },
    tests: [
      { id: "cxr-pcp", name: "Chest X-Ray", icon: "🩻", clue: "Bilateral perihilar ground-glass opacities — 'bat wing' pattern. Notably milder than the clinical severity of hypoxaemia." },
      { id: "ct-pcp", name: "CT Chest (HRCT)", icon: "🩻", clue: "Bilateral ground-glass opacities with geographic distribution and 'crazy paving' — classic PCP pattern." },
      { id: "bal-pcp", name: "BAL — GMS Stain (Gomori Methenamine Silver)", icon: "🔬", clue: "Black-stained cysts with 'folded disc' or 'crushed ping pong ball' shape — Pneumocystis jirovecii cysts confirmed!" },
      { id: "ldh-pcp", name: "Serum LDH", icon: "🧪", clue: "LDH 680 U/L (markedly elevated) — correlates with disease severity and is a useful prognostic marker in PCP." },
      { id: "bglucan-pcp", name: "Beta-D-Glucan", icon: "🧪", clue: "Beta-D-glucan markedly elevated — cell wall component of Pneumocystis. Sensitive but not specific (also elevated in Candida, Aspergillus)." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Pneumocystis jirovecii — PCP Pneumonia" },
      { id: "wrong1", name: "SARS-CoV-2 — COVID-19 Pneumonia" },
      { id: "wrong2", name: "Streptococcus pneumoniae — Bacterial Pneumonia" },
      { id: "wrong3", name: "Mycobacterium tuberculosis — Pulmonary TB" },
    ],
    correctDiagnosis: "correct",
  },

  "acinetobacter": {
    presentation: {
      intro: "A 74-year-old ventilated ICU patient (day 12 of mechanical ventilation, multiple prior antibiotics) develops fever, increased purulent green secretions, and worsening oxygenation. FiO2 up from 45% to 70%.",
      clues: ["Fever 39.1°C on day 12 of ventilation — late-onset VAP pattern", "Profuse green-tinged endotracheal secretions", "Multiple prior antibiotics this admission — MDR pathogen likely!"],
    },
    tests: [
      { id: "et-gram-ab", name: "ET Aspirate Gram Stain", icon: "🔬", clue: "Gram-negative coccobacilli — short rods, almost coccoid appearance. Distinctive morphology of Acinetobacter." },
      { id: "bal-cx-ab", name: "BAL Culture", icon: "🧫", clue: ">10⁵ CFU/mL Acinetobacter baumannii — meets quantitative threshold for VAP diagnosis." },
      { id: "sensitivity-ab", name: "Extended Antibiogram", icon: "🧪", clue: "Resistant to: carbapenems, cephalosporins, fluoroquinolones, aminoglycosides. Sensitive to: colistin only — XDR (extensively drug-resistant) strain!" },
      { id: "cxr-ab", name: "Chest X-Ray (Serial)", icon: "🩻", clue: "New bilateral infiltrates compared to yesterday's film — progressive consolidation consistent with VAP." },
      { id: "bac-cx-ab", name: "Blood Culture", icon: "🧫", clue: "Acinetobacter baumannii growing in blood — secondary bacteraemia from pulmonary source. Poor prognostic sign." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Acinetobacter baumannii — MDR VAP" },
      { id: "wrong1", name: "Pseudomonas aeruginosa — VAP" },
      { id: "wrong2", name: "MRSA — VAP" },
      { id: "wrong3", name: "Klebsiella pneumoniae — VAP" },
    ],
    correctDiagnosis: "correct",
  },

  "cmv": {
    presentation: {
      intro: "A 35-year-old HIV-positive man (CD4 count 42, not on ART) presents with 2 weeks of worsening floaters in his right eye and a new 'curtain' over his visual field.",
      clues: ["CD4 count 42 — below the 50 threshold for CMV retinitis risk", "Unilateral progressive visual field loss", "Ophthalmoscopy: yellow-white retinal lesions with haemorrhage — 'pizza-pie' or 'brushfire' retinitis"],
    },
    tests: [
      { id: "fundoscopy-cmv", name: "Dilated Fundoscopy", icon: "👁️", clue: "Full-thickness retinal necrosis with haemorrhage and exudates — 'pizza-pie' retinitis. Pathognomonic for CMV retinitis in this context!" },
      { id: "cmv-pcr", name: "CMV DNA PCR (Blood)", icon: "🧬", clue: "CMV viraemia 85,000 copies/mL — high viral load confirms active CMV disease." },
      { id: "cmv-pcr-vit", name: "CMV PCR (Vitreous Fluid)", icon: "🧬", clue: "CMV DNA detected in vitreous — localised ocular CMV infection confirmed." },
      { id: "cd4-cmv", name: "CD4 Count", icon: "🩸", clue: "CD4 42 — severely immunocompromised. CMV retinitis essentially only occurs below CD4 50." },
      { id: "toxo-cmv", name: "Toxoplasma IgG", icon: "🧪", clue: "NEGATIVE — Toxoplasma excluded. (Toxo causes ring-enhancing brain lesions, not retinitis.)" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Cytomegalovirus (CMV) — CMV Retinitis" },
      { id: "wrong1", name: "Toxoplasma gondii — Ocular Toxoplasmosis" },
      { id: "wrong2", name: "VZV — Acute Retinal Necrosis" },
      { id: "wrong3", name: "Candida albicans — Candidal Endophthalmitis" },
    ],
    correctDiagnosis: "correct",
  },

  "esbl-ecoli": {
    presentation: {
      intro: "A 70-year-old woman with recurrent UTIs, recently treated with cephalexin, presents with fever, rigors, and confusion. She has a urinary catheter in situ.",
      clues: ["Temp 39.6°C, HR 118, BP 92/58 — septic shock!", "Urinary catheter in situ × 3 weeks", "Previous UTIs treated with cephalosporins — antibiotic pressure selecting for resistance"],
    },
    tests: [
      { id: "blood-cx-esbl", name: "Blood Culture (×2)", icon: "🧫", clue: "Gram-negative rods growing — E. coli identified. Preliminary sensitivity: resistant to all cephalosporins!" },
      { id: "esbl-confirm", name: "ESBL Confirmatory Test (Double Disc)", icon: "🔬", clue: "Enhanced zone of inhibition with clavulanate — ESBL CONFIRMED. This organism destroys 3rd-gen cephalosporins." },
      { id: "urine-cx-esbl", name: "Urine Culture", icon: "🧫", clue: ">100,000 CFU/mL ESBL E. coli — urinary source of bacteraemia confirmed." },
      { id: "sensitivity-esbl", name: "Extended Sensitivity Panel", icon: "🧪", clue: "Resistant: amoxicillin, cephalosporins, pip-tazo (inoculum effect). Sensitive: ertapenem, meropenem, nitrofurantoin (for UTI only)." },
      { id: "ct-esbl", name: "CT Abdomen/Pelvis", icon: "🩻", clue: "No abscess or obstructed system. Catheter-associated bacteraemia — catheter must be removed!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "ESBL-Producing E. coli — Urosepsis" },
      { id: "wrong1", name: "Klebsiella pneumoniae — Bacteraemia" },
      { id: "wrong2", name: "Carbapenem-Resistant Enterobacteriaceae (CRE)" },
      { id: "wrong3", name: "MRSA — Catheter-Associated Bacteraemia" },
    ],
    correctDiagnosis: "correct",
  },

  "strongyloides": {
    presentation: {
      intro: "A 62-year-old Vietnamese man starts high-dose dexamethasone for cerebral oedema. Three weeks later he develops abdominal pain, bloody diarrhoea, and worsening confusion. Gram-negative rods found in his sputum.",
      clues: ["Recent high-dose corticosteroids — key trigger for hyperinfection!", "Gram-negative rods in sputum (larvae carrying gut bacteria to lungs)", "History from an endemic region — likely had asymptomatic Strongyloides for decades"],
    },
    tests: [
      { id: "stool-larvae", name: "Stool Microscopy (Direct)", icon: "🔬", clue: "Rhabditiform and filariform (L3) larvae visible in stool — massive larval burden! Hyperinfection confirmed." },
      { id: "sputum-larvae", name: "Sputum Microscopy", icon: "🔬", clue: "Strongyloides larvae AND gram-negative rods in sputum — larvae are physically carrying gut bacteria to the lungs!" },
      { id: "serology-ss", name: "Strongyloides IgG ELISA", icon: "🧪", clue: "Markedly elevated Strongyloides IgG — prior chronic infection now hyperactivating." },
      { id: "eosinophils-ss", name: "CBC — Eosinophil Count", icon: "🩸", clue: "Eosinophilia is ABSENT — steroids suppress eosinophils! Absence of eosinophilia does NOT rule out Strongyloides on steroids." },
      { id: "agar-plate-ss", name: "Agar Plate Culture (Stool)", icon: "🧫", clue: "Tracks of larvae visible on agar surface — larvae migrate through agar, leaving visible trails. Positive culture!" },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Strongyloides stercoralis — Hyperinfection Syndrome" },
      { id: "wrong1", name: "Clostridioides difficile Colitis" },
      { id: "wrong2", name: "Gram-Negative Sepsis (Primary)" },
      { id: "wrong3", name: "Inflammatory Bowel Disease Flare" },
    ],
    correctDiagnosis: "correct",
  },

  "cre": {
    presentation: {
      intro: "A 66-year-old transferred from a long-term care facility has a urinary catheter and a PICC line. Blood cultures from yesterday are flagging gram-negative growth. He remains febrile despite 48 hours of meropenem.",
      clues: ["Persistent fever DESPITE carbapenem therapy — MDR organism likely!", "Long-term care facility and recent hospitalisation — CRE risk factors", "PICC line and urinary catheter — multiple potential sources"],
    },
    tests: [
      { id: "blood-cx-cre", name: "Blood Culture — Extended Sensitivity", icon: "🧫", clue: "Klebsiella pneumoniae growing. Carbapenem MIC >16 μg/mL — carbapenem-resistant! KPC enzyme suspected." },
      { id: "kpc-pcr", name: "KPC Gene PCR", icon: "🧬", clue: "KPC gene (bla-KPC) DETECTED — Klebsiella pneumoniae carbapenemase confirmed. Explains meropenem failure." },
      { id: "full-antibiogram", name: "Extended Antibiogram (CRE Panel)", icon: "🧪", clue: "Resistant: carbapenems, cephalosporins, fluoroquinolones, aminoglycosides. Sensitive: ceftazidime-avibactam, colistin." },
      { id: "urine-cx-cre", name: "Urine Culture", icon: "🧫", clue: "Same CRE Klebsiella in urine — catheter-associated UTI likely the portal of entry to bloodstream." },
      { id: "echo-cre", name: "Echocardiogram", icon: "🫀", clue: "No vegetations on PICC line or valves. Line-associated bacteraemia — PICC must be removed immediately." },
    ],
    diagnosisChoices: [
      { id: "correct", name: "Carbapenem-Resistant Klebsiella (CRE) — Bacteraemia" },
      { id: "wrong1", name: "ESBL-Producing E. coli — Bacteraemia" },
      { id: "wrong2", name: "Pseudomonas aeruginosa — Bacteraemia" },
      { id: "wrong3", name: "VRE — Bacteraemia" },
    ],
    correctDiagnosis: "correct",
  },
};
